import { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiX, FiEye } from 'react-icons/fi';
import { useDicomUploaderManager } from '../../../../services/pacs-system';
import ChatImageViewerBase from '../../../chat-session-info/ChatImageViewerBase';
import ChatImage from '../../../ChatImage';
import useDialogOpenState from '@minohealth/common/hooks/useDialogOpenState';
import { ModalitySelect } from '../../../chat-report-panel/ReportFormDialog';

/**
 * Multi-step modal for collecting missing patient fields across all pending_info files.
 *
 * @param {Object} props
 * @param {import('../../../../services/pacs-system').LeanDicomFileInfo[]} props.pendingFiles
 * @param {number} props.initialIndex  index into pendingFiles to start on
 * @param {() => void} props.onClose
 */
function MissingFieldsModal({ pendingFiles, initialIndex, onClose }) {
  const { provideInfo } = useDicomUploaderManager();

  const [currentPacsId, setCurrentPacsId] = useState(
    pendingFiles[Math.min(initialIndex, pendingFiles.length - 1)]?.pacsId ?? null
  );

  // Per-file field state, keyed by pacsId
  const [fieldMap, setFieldMap] = useState(() => {
    const map = {};
    for (const f of pendingFiles) {
      map[f.pacsId] = {
        modality: f.extractedFields?.modality || '',
        patientAge: f.extractedFields?.patientAge || '',
        patientGender: f.extractedFields?.patientGender || '',
        reasonForExam: '',
      };
    }
    return map;
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const imageViewerRef = useRef(null);
  const [_, setImageViewerOpen] = useDialogOpenState(imageViewerRef);

  // If the current item is removed from pendingFiles, move to nearest remaining item
  useEffect(() => {
    if (pendingFiles.length === 0) return;
    const stillExists = pendingFiles.some(f => f.pacsId === currentPacsId);
    if (!stillExists) {
      setCurrentPacsId(pendingFiles[0].pacsId);
    }
  }, [pendingFiles]);

  // Sync new pending files into fieldMap as they arrive
  useEffect(() => {
    setFieldMap(prev => {
      const next = { ...prev };
      for (const f of pendingFiles) {
        if (!next[f.pacsId]) {
          next[f.pacsId] = {
            modality: f.extractedFields?.modality || '',
            patientAge: f.extractedFields?.patientAge || '',
            patientGender: f.extractedFields?.patientGender || '',
            reasonForExam: '',
          };
        }
      }
      return next;
    });
  }, [pendingFiles]);

  if (pendingFiles.length === 0) return null;

  const currentIndex = pendingFiles.findIndex(f => f.pacsId === currentPacsId);
  const current = pendingFiles[currentIndex >= 0 ? currentIndex : 0];
  if (!current) return null;

  const currentFields = fieldMap[current.pacsId] ?? {};
  const totalCount = pendingFiles.length;

  function handleFieldChange(name, value) {
    setFieldMap(prev => ({
      ...prev,
      [current.pacsId]: { ...prev[current.pacsId], [name]: value },
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {};
    if (current.missingFields.includes('modality')) payload.modality = currentFields.modality;
    if (current.missingFields.includes('patientAge')) payload.patientAge = currentFields.patientAge;
    if (current.missingFields.includes('patientGender')) payload.patientGender = currentFields.patientGender;
    if (current.missingFields.includes('reasonForExam')) payload.reasonForExam = currentFields.reasonForExam;

    try {
      // Capture neighbours before the item is removed from pendingFiles
      const nextFile = currentIndex < totalCount - 1 ? pendingFiles[currentIndex + 1] : null;
      const prevFile = currentIndex > 0 ? pendingFiles[currentIndex - 1] : null;

      await provideInfo(current.pacsId, payload);

      if (totalCount > 1) {
        const target = nextFile ?? prevFile;
        if (target) setCurrentPacsId(target.pacsId);
      } else {
        onClose(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const needs = (field) => current.missingFields.includes(field);

  return (
    <>
    <div className="pfi-modal__overlay" onClick={onClose}>
      <div className="pfi-modal__dialog" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="pfi-modal__header">
          <div className="pfi-modal__header-left">
            <h3 className="pfi-modal__title">Complete Patient Information</h3>
            {totalCount > 1 && (
              <span className="pfi-modal__counter">{currentIndex + 1} / {totalCount}</span>
            )}
          </div>
          <button className="pfi-modal__close" onClick={() => onClose(false)} title="Close">
            <FiX />
          </button>
        </div>

        {/* Navigation dots */}
        {totalCount > 1 && (
          <div className="pfi-modal__dots">
            {pendingFiles.map((f) => (
              <button
                key={f.pacsId}
                className={`pfi-modal__dot ${f.pacsId === currentPacsId ? 'pfi-modal__dot--active' : ''}`}
                onClick={() => setCurrentPacsId(f.pacsId)}
                title={f.file.name}
              />
            ))}
          </div>
        )}

        <div className="pfi-modal__body">
          {/* Image preview */}
          <div className="pfi-modal__preview">
            {current.result?.data?.image ? (
              <button
                className="pfi-modal__preview-btn"
                onClick={() => setImageViewerOpen(true)}
                title="View full image"
              >
                <ChatImage
                  srcImage={current.result.data.image}
                  thumbnail
                  className="pfi-modal__thumbnail"
                  alt={current.file.name}
                />
                <span className="pfi-modal__preview-hint">
                  <FiEye /> View
                </span>
              </button>
            ) : (
              <div className="pfi-modal__preview-placeholder">
                <FiEye />
                <span>No preview</span>
              </div>
            )}
            <p className="pfi-modal__filename" title={current.file.name}>
              {current.file.name}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="pfi-modal__form" key={current.pacsId}>
            <div className="pfi-modal__field">
              <span>Modality</span>
              <ModalitySelect
                key={current.pacsId}
                className="pfi-modal__modality-select"
                defaultSelectedValue={currentFields.modality || 'others'}
                onSelectModality={modality => handleFieldChange('modality', modality.value)}
                onChange={() => handleFieldChange('modality', '')}
                disabled={!needs('modality')}
              />
            </div>

            <label className="pfi-modal__field">
              <span>Age</span>
              <input
                type="number"
                min={0}
                max={150}
                value={currentFields.patientAge}
                onChange={e => handleFieldChange('patientAge', e.target.value)}
                disabled={!needs('patientAge')}
                required={needs('patientAge')}
              />
            </label>

            <label className="pfi-modal__field">
              <span>Gender (Sex)</span>
              <select
                value={currentFields.patientGender}
                onChange={e => handleFieldChange('patientGender', e.target.value)}
                disabled={!needs('patientGender')}
                required={needs('patientGender')}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <label className="pfi-modal__field">
              <span>Reason for Examination</span>
              <textarea
                value={currentFields.reasonForExam}
                onChange={e => handleFieldChange('reasonForExam', e.target.value)}
                rows={3}
                placeholder="Describe the reason for this examination"
                disabled={!needs('reasonForExam')}
                required={needs('reasonForExam')}
              />
            </label>

            {error && <p className="pfi-modal__error">{error}</p>}

            <div className="pfi-modal__footer">
              {/* Left / right navigation */}
              <div className="pfi-modal__nav">
                <button
                  type="button"
                  className="pfi-modal__nav-btn"
                  onClick={() => currentIndex > 0 && setCurrentPacsId(pendingFiles[currentIndex - 1].pacsId)}
                  disabled={currentIndex <= 0}
                  title="Previous"
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  className="pfi-modal__nav-btn"
                  onClick={() => currentIndex < totalCount - 1 && setCurrentPacsId(pendingFiles[currentIndex + 1].pacsId)}
                  disabled={currentIndex >= totalCount - 1}
                  title="Next"
                >
                  <FiChevronRight />
                </button>
              </div>

              <button
                type="submit"
                className="pfi-modal__submit"
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : totalCount > 1 ? 'Submit & Next' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>

    {/* Viewer outside overlay so its close events don't bubble to overlay's onClick */}
    {current.result?.data?.image && (
      <ChatImageViewerBase
        srcImage={current.result.data.image}
        multiple={false}
        bboxes={[]}
        onCloseViewer={() => setImageViewerOpen(false)}
        ref={imageViewerRef}
      />
    )}
    </>
  );
}

export default MissingFieldsModal;
